import React, { useEffect, useState } from 'react';
import PropertyFields from './PropertyFields';
import { Chip } from './StatusChip';
import { useConfirm } from './ConfirmDialog';
import { dueStatus } from '../utils/compliance';
import { formatDate } from '../utils/format';
import { apiErrorMessage } from '../hooks/useApi';

/**
 * <PropertyProfileSection /> — one contractor-fillable section (About / Access /
 * SHEQ / Contractor / Related) with a read-only view + Edit/Save toggle, using
 * the shared config-driven PropertyFields form.
 *
 * Saved answers show as facts. A field nobody has filled in but which has a
 * `suggest` value shows that value tagged "Suggested — not confirmed", never as
 * a fact; in edit mode it's offered as "Use suggestion".
 *
 * `onSave(sectionData)` persists (the caller decides where) and must THROW on
 * failure: the section then stays in edit mode with the error shown, so nothing
 * typed is lost.
 */
const isEmpty = (v) => v == null || v === '' || (Array.isArray(v) && !v.length);
const sameValues = (a, b) => JSON.stringify(a || {}) === JSON.stringify(b || {});

const PropertyProfileSection = ({ title, spec = [], values, saved, note, onSave, showEmptyDates = false }) => {
  const { confirm } = useConfirm();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(values || {});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { if (!editing) setDraft(values || {}); }, [values, editing]);

  const answered = spec.filter((f) => !isEmpty(values?.[f.key]));
  const suggested = spec.filter((f) => isEmpty(values?.[f.key]) && f.suggest != null);
  const hasAny = answered.length > 0;
  const dirty = editing && !sameValues(draft, values);

  const start = () => { setDraft({ ...(values || {}) }); setError(null); setEditing(true); };
  const cancel = async () => {
    if (dirty && !(await confirm({ title: 'Discard changes?', body: `Your edits to “${title}” haven't been saved.`, confirmLabel: 'Discard changes', tone: 'danger' }))) return;
    setEditing(false); setError(null); setDraft(values || {});
  };
  const save = async () => {
    setSaving(true); setError(null);
    try { await onSave(draft); setEditing(false); }
    catch (e) { setError(apiErrorMessage(e, "Couldn't save — your changes are still here, try again")); }
    finally { setSaving(false); }
  };
  const footNote = note || 'Saved for this property; only your company sees it.';

  return (
    <section className="cw-card cw-card-pad" aria-label={title}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: editing ? 12 : 8, gap: 10, flexWrap: 'wrap' }}>
        <h3 className="cw-h3" style={{ margin: 0 }}>{title}</h3>
        {!editing ? (
          <button type="button" className="cw-btn cw-btn-secondary" onClick={start}>
            <i className={`fa-solid ${saved || hasAny ? 'fa-pen' : 'fa-plus'}`} aria-hidden="true" /> {saved || hasAny ? 'Edit' : 'Fill in'}
          </button>
        ) : (
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="cw-btn cw-btn-secondary" onClick={cancel} disabled={saving}>Cancel</button>
            <button type="button" className="cw-btn cw-btn-primary" onClick={save} disabled={saving} aria-busy={saving || undefined}>{saving ? 'Saving…' : 'Save'}</button>
          </div>
        )}
      </div>
      {editing ? (
        <>
          <p className="cw-meta" style={{ margin: '0 0 12px' }}>Suggested answers are shown under each field — confirm them with “Use suggestion”, then Save. {footNote}</p>
          {error && <div className="cw-error-state compact" role="alert" style={{ marginBottom: 12 }}><div className="cw-error-state-text"><p className="cw-error-state-title">Not saved</p><p className="cw-error-state-reason">{error}</p></div></div>}
          <PropertyFields spec={spec} values={draft} onChange={(k, v) => setDraft((d) => ({ ...d, [k]: v }))} />
        </>
      ) : (hasAny || suggested.length > 0 || (showEmptyDates && spec.some((f) => f.type === 'date'))) ? (
        <>
          <dl style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(200px, 100%), 1fr))', gap: 14, margin: 0 }}>
            {spec.map((f) => {
              const v = values?.[f.key];
              const isDate = f.type === 'date';
              const empty = isEmpty(v);
              const isSuggestion = empty && f.suggest != null;
              if (empty && !isSuggestion && !(isDate && showEmptyDates)) return null;
              const shown = isSuggestion ? f.suggest : v;
              const disp = Array.isArray(shown) ? shown.join(', ') : shown;
              return (
                <div key={f.key} style={f.full ? { gridColumn: '1 / -1' } : undefined}>
                  <dt className="cw-label" style={{ marginBottom: 2 }}>{f.label}</dt>
                  <dd style={{ margin: 0 }}>
                    {isDate && !isSuggestion ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 'var(--cs-text-base)', fontWeight: 600 }}>{empty ? '—' : formatDate(disp)}</span>
                        {(() => { const s = dueStatus(disp); return <Chip tone={s.tone} label={s.label} />; })()}
                      </div>
                    ) : isSuggestion ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 'var(--cs-text-base)', color: 'var(--cw-text-muted)', fontStyle: 'italic' }}>{String(disp)}</span>
                        <Chip tone="warning" label="Suggested — not confirmed" />
                      </div>
                    ) : (
                      <div style={{ fontSize: 'var(--cs-text-base)', fontWeight: 600, marginTop: 2 }}>{String(disp)}</div>
                    )}
                  </dd>
                </div>
              );
            })}
          </dl>
          {!hasAny && <p className="cw-meta" style={{ margin: '12px 0 0' }}>Nothing confirmed yet — choose <strong>Fill in</strong>. {footNote}</p>}
        </>
      ) : (
        <p className="cw-meta" style={{ margin: 0 }}>Not filled in yet — choose <strong>Fill in</strong>. {footNote}</p>
      )}
    </section>
  );
};

export default PropertyProfileSection;
